import React, { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './AddCategoryGroupDialog.css';

function AddCategoryGroupDialog({ isOpen, onClose, onSuccess, editGroup }) {
  const [groupSource, setGroupSource] = useState('category'); // 'category' or 'account'
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const user = auth.currentUser;

  useEffect(() => {
    if (editGroup) {
      setGroupSource(editGroup.source || 'category');
      setName(editGroup.name || '');
      setType(editGroup.type || '');
      setActive(editGroup.active !== false);
    } else {
      // Reset form for new group
      setGroupSource('category');
      setName('');
      setType('');
      setActive(true);
    }
  }, [editGroup, isOpen]);

  const generateRandomId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const generateDocId = (source, type) => {
    const randomId = generateRandomId();
    
    if (source === 'account') {
      if (type === 'Asset') return `A-GRP-${randomId}`;
      if (type === 'Liability') return `L-GRP-${randomId}`;
    }
    
    if (source === 'category') {
      if (type === 'Expense') return `EXP-GRP-${randomId}`;
      if (type === 'Income') return `INC-GRP-${randomId}`;
    }
    
    return randomId; // Fallback
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Please enter a group name');
      return;
    }
    
    if (!type) {
      alert('Please select a type');
      return;
    }

    setSaving(true);

    try {
      const collectionName = groupSource === 'account' ? 'account_groups' : 'category_groups';
      
      // Use existing ID when editing, generate new one when creating
      const docId = editGroup ? editGroup.id : generateDocId(groupSource, type);
      
      const groupRef = doc(db, 'users', user.uid, collectionName, docId);
      
      await setDoc(groupRef, {
        name: name.trim(),
        type,
        active
      });

      alert(`Group ${editGroup ? 'updated' : 'created'} successfully!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving group:', error);
      alert('Failed to save group: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const getTypeOptions = () => {
    if (groupSource === 'account') {
      return ['Asset', 'Liability'];
    }
    return ['Expense', 'Income'];
  };

  return (
    <div className="modal-overlay" style={{ top: '93px' }}>
      <div className="modal-content category-group-modal">
        <div className="modal-header">
          <h2>{editGroup ? 'Edit Category Group' : 'Add Category Group'}</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            
            {/* Source Selection - Only show when creating new */}
            {!editGroup && (
              <div className="form-group">
                <label>Group Source *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="category"
                      checked={groupSource === 'category'}
                      onChange={(e) => {
                        setGroupSource(e.target.value);
                        setType(''); // Reset type when source changes
                      }}
                    />
                    <span>📁 Category Group</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="account"
                      checked={groupSource === 'account'}
                      onChange={(e) => {
                        setGroupSource(e.target.value);
                        setType(''); // Reset type when source changes
                      }}
                    />
                    <span>🏦 Account Group</span>
                  </label>
                </div>
              </div>
            )}

            {/* Name */}
            <div className="form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter group name"
                required
              />
            </div>

            {/* Type */}
            <div className="form-group">
              <label>Type *</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                required
              >
                <option value="">Select type</option>
                {getTypeOptions().map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Active Status */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>Active</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editGroup ? 'Update Group' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCategoryGroupDialog;